import { apiGet, API_BASE } from './api';

/**
 * Re-fetch a single order via GET /api/orders/:id and merge it into the
 * local orders state in place — used after an action (approve/reject/
 * status change/cancel) so the user doesn't lose scroll position to a
 * full-list re-fetch.
 *
 * Strategy: we keep the existing list-row's `order_total`, `item_count`,
 * `first_item_*`, and `min_slack_days` (the detail endpoint doesn't
 * compute these), and overwrite only the fields the action just changed —
 * SIX of them: `status`, `cancellation_reason`, `rejection_reason`,
 * `admin_notes`, `completed_at` and `completed_by`. `can_action` is
 * cleared because after acting on an order the same user usually can't
 * action it again at this step.
 *
 * ── 🔁 `completed_at` / `completed_by` JOINED THE MERGE 2026-09-12 ──────
 * The enumeration above named FOUR fields until then, and `Actions ▾ → Mark
 * Complete` was the bug that exposed the gap: `PATCH /api/orders/:id/complete`
 * writes both columns server-side, `handleAction` refreshes the row through
 * here, and the list row kept `completed_at: null` until the next full
 * refetch. Everything downstream reads that ONE column — `isOrderComplete`
 * (`constants/statuses.js`) drives the `✓ Complete` badge, the chevron
 * tooltip, and `orderQueueTab`, so the queue-tab counts left the row under
 * `To complete` and the `Completed` tab never received it. `mode: 'reopen'`
 * was the mirror: the server clears both columns and the row kept its stale
 * timestamp.
 *
 * 🔑 THE TEST FOR WHAT MAY JOIN THIS MERGE IS "is it a COLUMN on `orders`",
 * not "did an action change it". The four originals and these two are all
 * plain `orders` columns, carried by `SELECT o.*` on BOTH the list endpoints
 * (`GET /api/orders`, `GET /api/orders/department`) and the detail one, so
 * copying them across replaces a value with the SAME value's fresher
 * generation. `order_total` / `item_count` / `first_item_*` / `min_slack_days`
 * are list-only COMPUTED fields the detail endpoint never builds, so an
 * explicit `order_total: fresh.order_total` would overwrite a real number with
 * `undefined` — that is the contract this list is narrow to protect, and it is
 * unchanged.
 * 🚨 **THE SHAPE THAT ACTUALLY THREATENS IT IS `...fresh`, NOT AN EXPLICIT
 * ASSIGNMENT, AND IT IS THE ONE A "tidy-up" REACHES FOR.** Spreading the whole
 * payload after `...o` does NOT clobber the computed fields — they are ABSENT
 * from the detail body, so `o`'s values survive and every "did not become
 * `undefined`" assertion stays green. What it DOES do is land
 * `can_edit` / `can_manage` / `is_personal_workspace` / `items` / `files` on the
 * LIST row, which is exactly what the root `CLAUDE.md`'s `OrderInlineEditor.js`
 * bullet forbids ("and **never `can_edit`**") — a stale caller-derived flag
 * reaching the row is the defect that bullet exists to record. **So the pin is
 * ABSENCE of those keys, not survival of the numbers**; §3 of
 * `refreshOneOrder.completion.test.js` asserts both, having been proven green
 * against the spread when it asserted only the second (2026-09-12 review).
 * 🪤 There is no `completed_by_name` anywhere in this codebase (`completed_by`
 * is a bare user id and nothing renders it) — checked, not assumed, because
 * copying an id without its display name is how a row ends up naming nobody.
 *
 * ── ⚠️ `clearCanAction` — AND WHY THE DEFAULT IS `true` ────────────────
 * `can_action` is NOT on the detail payload. `GET /api/orders/:id` returns
 * `{ ...order, items, files, can_edit, can_manage, is_personal_workspace }` —
 * read that handler's `res.json`, never a count from here: this said "computes
 * exactly ONE caller-dependent flag" long after `can_manage` landed, and cited
 * `server/routes/orders.js:1104`, which is a COMMENT (both corrected
 * 2026-09-12; the root `CLAUDE.md`'s twin of this paragraph had already been).
 * **The point is that `can_action` is NOT among them**: it is computed per role
 * by the LIST endpoints alone (CLAUDE.md, "Actionable Definition"). So this
 * helper cannot READ a fresh `can_action` — it can only assert one, and `false`
 * is an assertion, not a refresh.
 *
 * That assertion is right for the FOUR ACTION callers (`handleAction` on
 * DepartmentOrders, THREE in MyOrders): each has just consumed the approval step
 * or status transition that made the order actionable, so `false` is true
 * immediately and the next list refetch merely confirms it.
 * 🔻 **CORRECTED 2026-09-13 — this said FIVE, and four in MyOrders.** Counted
 * from the tree, not from the prose. 🪤 **And none of the four calls
 * `refreshOneOrder` directly**, which is why grepping that name finds only the
 * three EDIT sites passing `{ clearCanAction: false }` and reads as though no
 * action caller exists at all: every one of them goes through `refreshOrderRow`
 * below, which defaults `clearCanAction = true` and forwards it. **Grep
 * `refreshOrderRow(` as well before quoting any count of either.**
 *
 * It is WRONG for an EDIT. `handleEditorSaved` follows a `PUT /api/orders/:id`,
 * which consumes no approval step, so stamping `false` invents a claim and the
 * user loses an action button they still hold until `fetchOrders(true)` lands —
 * and if that call is slow or fails, they lose it for good. Pass
 * `{ clearCanAction: false }` there.
 *
 * 🔑 `false` means "ASSERT NOTHING", not "assert unchanged", and the difference
 * matters in one real case: `PUT /api/orders/:id` resubmits a REJECTED order
 * (`newStatus = order.status === 'rejected' ? 'submitted' : order.status`), and
 * that transition genuinely does change `can_action`. Neither clearing nor
 * preserving is correct there. Preserving is how we express "this helper has no
 * opinion" and leave the answer to the only thing that can compute it — the
 * list refetch the caller already makes.
 *
 * The default is `true` so every existing call site is byte-identical.
 *
 * ── 🔻 RELOCATED 2026-09-21 FROM `client/src/utils/CLAUDE.md`, VERBATIM ─────
 * That topic file is an AUTO-LOADED rule document with a 20,000-byte budget and
 * 128 bytes of headroom left; the text below is archaeology, so by owner ruling
 * it moved HERE, beside the code, BYTE-IDENTICAL. Nothing was rewritten or
 * trimmed in the move. It overlaps the prose above on purpose — the overlap is
 * the evidence the move lost nothing, and collapsing the two copies into one is
 * a separate decision nobody has made. The topic file keeps a pointer where
 * this stood.
 *
 * 🔻 **ONE FACTUAL CORRECTION APPLIED IN THE MOVE, MEASURED RATHER THAN COPIED, and the same wrong
 * sentence is corrected in `refreshOneOrder.js`'s own docblock in the same commit.** The prose said
 * *"the five ACTION callers (`handleAction` on DepartmentOrders, four in `MyOrders.js`)"*. Counted
 * from the tree: **FOUR — one on Department Orders and THREE in `MyOrders.js`.** 🪤 **And they do not
 * call `refreshOneOrder` at all**, which is why a grep for it finds only the three EDIT sites that
 * opt out and reads as though no action caller exists: every action caller goes through
 * **`refreshOrderRow`**, the sibling export, which defaults `clearCanAction = true` and forwards it.
 * Grep `refreshOrderRow(` as well as `refreshOneOrder(` before quoting any count here.
 *
 *   🔑 **`refreshOneOrder`'s fourth argument, added 2026-08-08 — `{ clearCanAction = true }`.** The stamp was never a refresh: **`can_action` is NOT on the detail payload.** `GET /api/orders/:id` computes SEVERAL caller-dependent fields — `can_edit`, `can_manage`, plus any display flags beside them; **read the handler's `res.json`, never a count from here** (this said *exactly ONE* long after `can_manage` landed; ⚠️ both halves also cited `routes/orders.js:1104`, a COMMENT). **The point is that `can_action` is NOT among them**: it is computed per role by the LIST endpoints alone. So writing `false` was an **assertion**, correct for the FOUR ACTION callers (`handleAction` on DepartmentOrders, THREE in `MyOrders.js`, all reaching this helper through `refreshOrderRow`) because each has just consumed the step that made the order actionable — and **false after an inline edit, which consumes no approval step**. The user lost the actionable outline and dropped out of the Actionable filter until `fetchOrders(true)` arrived to undo it, and for good if that call failed. Default `true`, so all four action sites are byte-identical. 🪞 `false` means **"assert nothing"**, not "assert unchanged": `PUT /api/orders/:id` resubmits a REJECTED order (`newStatus = order.status === 'rejected' ? 'submitted' : order.status`), which genuinely does change `can_action`, so neither clearing nor preserving is right there — preserving is how the helper says it has no opinion and leaves the answer to the list refetch. Pinned both ways by §12 of `DepartmentOrders.expandEdit.test.js` (the edit must NOT clear it, an action MUST), with the list refetch stalled in the test because otherwise it repairs the bogus value before any assertion can see it.
 *
 * @param {number|string} orderId
 * @param {Function} setOrders         setState for the orders[] array
 * @param {Function} [setOrderDetails] optional setState for an orderDetails cache
 * @param {Object}   [options]
 * @param {boolean}  [options.clearCanAction=true] stamp `can_action: false` on
 *        the list row. Pass `false` for a mutation that consumes no approval
 *        step, which leaves the field untouched for the list refetch to answer.
 * @returns {Promise<object|null>} the fresh order, or null on error
 */
export async function refreshOneOrder(orderId, setOrders, setOrderDetails, { clearCanAction = true } = {}) {
  try {
    const res = await apiGet(`${API_BASE}/api/orders/${orderId}`);
    if (!res.ok) return null;
    const fresh = await res.json();

    setOrders(prev => prev.map(o => {
      if (o.id !== orderId) return o;
      const merged = {
        ...o,
        status: fresh.status,
        cancellation_reason: fresh.cancellation_reason,
        rejection_reason: fresh.rejection_reason,
        admin_notes: fresh.admin_notes,
        // 2026-09-12 — the completion OVERLAY, a pair of real `orders` columns
        // that `PATCH /:id/complete` sets and clears. Both directions matter:
        // `null` from a reopen must overwrite a stale timestamp, so these are
        // straight assignments and never `fresh.x || o.x`.
        completed_at: fresh.completed_at,
        completed_by: fresh.completed_by,
      };
      // Omitted rather than copied from `o`: `can_action` is absent from the
      // detail payload, so writing it at all is an assertion. Leaving the key
      // untouched is the only way to say nothing about it.
      if (clearCanAction) merged.can_action = false;
      return merged;
    }));

    if (setOrderDetails) {
      setOrderDetails(od => ({ ...od, [orderId]: fresh }));
    }
    return fresh;
  } catch {
    return null;
  }
}

/**
 * THE resilient sequence for correcting one row after a MUTATING ACTION
 * (approve/reject/cancel/complete/reopen/…) — added 2026-08-27 after a live
 * production report: an order Jake had just approved kept showing Approve on
 * re-expanding it. Root cause: `handleAction`-style callers used to call
 * `refreshOneOrder` ALONE as their only refresh, and it has exactly one
 * silent failure mode — a non-ok response or a thrown/rejected `apiGet` are
 * both caught internally and turned into a `null` return that touches
 * NEITHER `setOrders` NOR `setOrderDetails` (see its docblock above). A
 * dropped connection on that one GET, immediately after an otherwise
 * successful mutation, left the row confidently wrong with nothing on
 * screen saying so — and it could not self-heal on a later re-expand either,
 * because the stale cache entry was never touched, so `toggleExpand`'s
 * `!orderDetails[orderId] && !detailsLoading[orderId]` guard (both
 * `DepartmentOrders.js` and `MyOrders.js`) found it non-empty and skipped
 * fetching, replaying the same stale payload forever.
 *
 * 🔑 **THE CACHE DELETE IS UNCONDITIONAL AND FIRST — FAIL TO EMPTY, NEVER FAIL
 * TO STALE.** The cache entry is dropped SYNCHRONOUSLY *before* either network
 * call, so if `refreshOneOrder`'s GET fails the cache is left EMPTY rather
 * than stale — and an empty cache is exactly what `toggleExpand` already
 * knows how to recover from on its own, via its normal fetch-when-empty path.
 * Making this step conditional on anything reopens the hole this function
 * exists to close, because a "delete on failure" step is itself a SECOND
 * thing that can be skipped by the same class of dropped request — there is
 * no local value yet to condition it on. **Do not touch this step's
 * unconditionality.**
 *
 * 🔀 **THE LIST REFETCH IS CONDITIONAL ON `refreshOneOrder` HAVING FAILED —
 * CHANGED 2026-08-27, AFTER STAGING WENT RED.** `e2e/online-order-action-no-refetch.spec.js`
 * pins a deliberate COST contract: a single-row action refreshes only that
 * order, never the whole list — a real GET added to every action for every
 * user on all three pages is not free, and the client suite cannot see that
 * spec (it is `@smoke`, staging-only). Making the list refetch unconditional
 * to close the stale-row bug violated that contract on every SUCCESSFUL
 * action, which is the overwhelmingly common case. The fix keeps the fallback
 * for exactly the case it exists for and drops it everywhere else:
 * `refreshOneOrder`'s return is held in a local — `null` on both its failure
 * paths (a non-ok response and the catch-all around a thrown/rejected
 * `apiGet`; see its own docblock), the fresh order object on every success —
 * and `fetchList` runs only when that local is falsy.
 *
 * ⚠️ **THIS ASYMMETRY WITH THE CACHE-DELETE STEP IS DELIBERATE AND IS NOT THE
 * SAME REASONING APPLIED INCONSISTENTLY — READ BOTH PARAGRAPHS TOGETHER
 * BEFORE "FIXING" EITHER TO MATCH THE OTHER.** The cache delete has to stay
 * unconditional because conditioning it on failure would need a SECOND
 * network round trip to have already told you whether the first one worked —
 * exactly the kind of request that can itself vanish, which is the original
 * hole. The list refetch has no equivalent problem: by the time it is
 * considered, the `await` has already resolved and the fresh-or-null value is
 * sitting in a local variable — there is no further request between "decide"
 * and "act" for a dropped connection to intercept. **A "delete on failure"
 * step is unsound because the failure signal is a REQUEST that can fail too;
 * a "refetch on failure" step is sound because the failure signal is a VALUE
 * already held.** Collapsing this distinction — making the delete conditional
 * to "match" the refetch, or making the refetch unconditional to "match" the
 * delete — reopens one hole or the other.
 *
 * This is deliberately a SEPARATE function from `refreshOneOrder`, not a new
 * option on it: `handleEditorSaved` on both pages already performs this same
 * shape by hand (for a different reason — a PUT consumes no approval step,
 * so it passes `clearCanAction: false` — and its own synchronous delete has
 * to run even when the rest of the cascade is skipped by the save-then-act
 * short-circuit, so it cannot simply delegate here; its own list refetch
 * stays UNCONDITIONAL, because that page's cost contract is a different one —
 * see its own docblock). Every ACTION call site should use this, not the bare
 * `refreshOneOrder` + a hand-rolled sequel.
 *
 * @param {number|string} orderId
 * @param {Object} deps
 * @param {Function} deps.setOrders          setState for the orders[] array
 * @param {Function} [deps.setOrderDetails]  setState for the orderDetails cache
 * @param {Function} [deps.fetchList]        the page's full list refetch,
 *        e.g. `() => fetchOrders(true)`. Awaited ONLY when `refreshOneOrder`
 *        returned falsy — never on a successful per-order refresh.
 * @param {boolean}  [deps.clearCanAction=true] forwarded to `refreshOneOrder`.
 */
export async function refreshOrderRow(orderId, { setOrders, setOrderDetails, fetchList, clearCanAction = true } = {}) {
  if (setOrderDetails) {
    setOrderDetails(od => {
      if (!(orderId in od)) return od;
      const copy = { ...od };
      delete copy[orderId];
      return copy;
    });
  }
  const fresh = await refreshOneOrder(orderId, setOrders, setOrderDetails, { clearCanAction });
  // `fresh` is the order object on every success (server/routes/orders.js's
  // GET /api/orders/:id has exactly one response shape,
  // `{ ...order, items, files, can_edit }`, always truthy) and `null` on
  // both of `refreshOneOrder`'s failure paths — see the docblock above.
  if (!fresh && fetchList) await fetchList();
}

/**
 * A `setOrders` that ALSO reports "an order changed" — the hires pages' half of
 * `refreshOrderRow`'s cost contract (owner's staging feedback 2026-09-29: the
 * Hires tab counts and the paperclip did not move until a reload).
 *
 * `refreshOrderRow` refetches the order LIST only when its per-order refresh
 * FAILS, which is right for an order page (its list IS the order rows it just
 * merged). A hires page lists HIRE rows from a different endpoint
 * (`GET /api/hires`), which no per-order merge can reach — so on the common,
 * successful path its tabs, counts and markers stayed stale. Every mutation
 * path in the expansion hooks writes the order row through `setOrders`, so
 * wrapping it here is the ONE funnel: `onOrderMutated` fires after the write,
 * and the hires page answers with its SILENT refetch.
 *
 * `onOrderMutated` absent → returns `setOrders` ITSELF (same identity), so the
 * order pages are unchanged. 🔑 The hooks' `refreshOrderDetail` (called BY a
 * hires page after its own action) keeps the RAW setter — reporting that back
 * would bounce the page's refresh into a second refetch.
 *
 * 🔒 THE CLASS RATCHET: `server/tests/regressions/order-mutation-list-refresh-manifest.test.js`
 * enumerates every `refreshOrderRow(` / `mergeStatusJumpResult(` call and every
 * `onFilesChanged={…}` in `client/src` and requires a list refresh after each,
 * or a declared reason. A new call site of either export owes it one or the other.
 *
 * @param {Function} setOrders       the caller's `useState` setter
 * @param {Function|null} onOrderMutated  called (no arguments) after each write
 */
export function withOrderMutationNotice(setOrders, onOrderMutated) {
  if (!onOrderMutated) return setOrders;
  return (update) => {
    setOrders(update);
    onOrderMutated();
  };
}

/**
 * Merge a `PATCH /api/orders/:id/status` response into a page's two state
 * shapes — the list row and the `orderDetails` cache — in place.
 *
 * THE `InlineStatusJump` `onSaved` HANDLER, EXTRACTED 2026-09-11 when My Orders
 * gained the status override and became its second caller. It was one hand-rolled
 * copy on `DepartmentOrders.js`; copying it a second time is what
 * `npm run check-drift` / the semantic-duplication ratchet exist to refuse, and
 * the house rule is to expand a good idea as SHARED CODE, never by copying.
 *
 * 🔑 IT MERGES EXACTLY TWO FIELDS AND MUST NOT GROW A THIRD WITHOUT A REASON.
 * `status` and `rejection_reason` are what that route changes and what the two
 * pages render from the row. It deliberately does NOT touch `can_action` (only a
 * LIST endpoint computes that — see `refreshOneOrder`'s docblock above), does not
 * touch `can_edit`, and does not touch `can_manage`: all three are caller-derived
 * flags a PATCH response has no authority over, and asserting one here is the
 * mistake `clearCanAction` exists to document.
 *
 * 🪤 The details branch is `prev[id] ? {…} : prev` — it must never CREATE a cache
 * entry. `toggleExpand` fetches only when the entry is absent, so inventing a
 * partial one here would make the page serve a two-field order for ever.
 *
 * @param {Object} updated the PATCH response body (needs `id`, `status`, `rejection_reason`)
 * @param {Function} setOrders setState for the `orders[]` array
 * @param {Function} setOrderDetails setState for the `orderDetails` cache
 */
export function mergeStatusJumpResult(updated, setOrders, setOrderDetails) {
  setOrders(prev => prev.map(o => (o.id === updated.id
    ? { ...o, status: updated.status, rejection_reason: updated.rejection_reason }
    : o)));
  setOrderDetails(prev => (prev[updated.id]
    ? { ...prev, [updated.id]: { ...prev[updated.id], status: updated.status, rejection_reason: updated.rejection_reason } }
    : prev));
}
